import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { LinkButton, LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageShowAllLink
} from 'components/page';
import { PathsSortBy, PathsTile } from '.';

import type { GetPathsRes } from 'shared-types/responses';
import type { ViewProps } from 'types';

type Props = ViewProps & {
  onFilterDataReceived?: (
    data: GetPathsRes['pathsFilterOptions'] | null
  ) => void;
};

const PathsViewUser = ({
  defaultSort = 'active',
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 8,
  onFilterDataReceived,
  onNoData = null,
  queryParams = '',
  showAllLinkHref = null,
  title,
  titleShowCount = true
}: Props) => {
  const { messages, user } = useReduxState();

  const router = useRouter();

  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(defaultSort);

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  useEffect(() => {
    const routerSort = router.query.sortBy;
    if (routerSort && routerSort !== sortBy) setSortBy(routerSort as string);
  }, [router.query.sortBy]);

  const sortByInQueryParams = queryParams?.includes('&sortBy=');

  const { data, loading } = useApiData<GetPathsRes>({
    onDataUpdated: (data) => {
      // filter data is sent to parent
      if (onFilterDataReceived) {
        if (data?.pathsFilterOptions) {
          onFilterDataReceived(data?.pathsFilterOptions);
        } else {
          onFilterDataReceived(null);
        }
      }
    },
    url:
      `/admin/paths?pathType=active` +
      `&limit=${limitPerPage}` +
      `&page=${page}` +
      queryParams +
      (sortByInQueryParams || sortBy === 'alphabetic'
        ? ''
        : `&sortBy=${sortBy}`),
    onlyIf: Boolean(user)
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.paths?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return loading ? (
    <LoadingIndicator />
  ) : data?.pathsCount > 0 ? (
    <div className="content">
      <PageSectionHeader
        description={description}
        title={title || messages.pathsMyPaths}
        titleCount={titleShowCount ? data?.pathsCount || 0 : undefined}
      >
        {isHomeView ? null : (
          <PathsSortBy
            options={[
              { text: messages.commonAlphabetic, value: 'alphabetic' },
              { text: messages.courseLastOpened, value: 'active' }
            ]}
            sortBy={sortBy}
          />
        )}
      </PageSectionHeader>

      <div className={`paths ${isHomeView ? 'paths-in-home-view' : ''}`}>
        {data.paths.map((path) => (
          <div className="path" key={path.id}>
            <PathsTile path={path} pathType="user" />
          </div>
        ))}
      </div>

      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages}
          onPageChange={({ page }) => setPage(page)}
        />
      ) : showAllLinkHref && data?.pathsCount > data?.paths?.length ? (
        <PageShowAllLink
          href={showAllLinkHref}
          numberOfItems={data?.pathsCount}
        />
      ) : null}

      <style jsx>{`
        .paths {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
        }
        .path {
          width: 100%;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .paths-in-home-view .path {
            width: calc(50% - 15px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .path {
            width: calc(50% - 15px);
          }
          .paths-in-home-view .path {
            width: calc(33.333% - 20px);
          }
        }
      `}</style>
    </div>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.pathsMyPaths}
        titleCount={titleShowCount ? 0 : undefined}
      />

      <PageEmptyIndicator
        icon="tasks"
        title={messages.pathsEmpty}
        description={messages.pathsEmptyActive}
      >
        <LinkButton
          category="light"
          href="/paths?type=available"
          size="small"
          text={messages.pathsSeeAvailablePaths}
        />
      </PageEmptyIndicator>
    </>
  );
};

export default PathsViewUser;
